// =============================================================================
// ProviderOverviewTab — Edit form for provider details
// =============================================================================

import { useState, useEffect, type ChangeEvent, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Select } from '@tkpod/ui';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProviderData {
  id: string;
  name: string;
  slug: string;
  type: string;
  status: string;
  capabilities: Record<string, unknown>;
  processingTime: number;
  dispatchDelayMinutes: number;
  notifyShopifyOnLabelCreated: boolean;
  country: string | null;
  timezone: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  apiEndpoint: string | null;
  notes: string | null;
}

interface ProviderOverviewTabProps {
  provider: ProviderData;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TYPE_OPTIONS = [
  { value: 'INTERNAL', label: 'Internal' },
  { value: 'EXTERNAL_API', label: 'External API' },
  { value: 'MANUAL', label: 'Manual' },
];

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProviderOverviewTab({ provider }: ProviderOverviewTabProps) {
  const queryClient = useQueryClient();

  const [name, setName] = useState(provider.name);
  const [type, setType] = useState(provider.type);
  const [status, setStatus] = useState(provider.status);
  const [processingTime, setProcessingTime] = useState(String(provider.processingTime));
  const [dispatchDelayMinutes, setDispatchDelayMinutes] = useState(
    String(provider.dispatchDelayMinutes ?? 0),
  );
  const [notifyShopifyOnLabelCreated, setNotifyShopifyOnLabelCreated] = useState(
    provider.notifyShopifyOnLabelCreated ?? false,
  );
  const [country, setCountry] = useState(provider.country ?? '');
  const [timezone, setTimezone] = useState(provider.timezone ?? '');
  const [contactEmail, setContactEmail] = useState(provider.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(provider.contactPhone ?? '');
  const [apiEndpoint, setApiEndpoint] = useState(provider.apiEndpoint ?? '');
  const [notes, setNotes] = useState(provider.notes ?? '');
  const [capabilitiesJson, setCapabilitiesJson] = useState(
    JSON.stringify(provider.capabilities, null, 2),
  );

  useEffect(() => {
    setName(provider.name);
    setType(provider.type);
    setStatus(provider.status);
    setProcessingTime(String(provider.processingTime));
    setDispatchDelayMinutes(String(provider.dispatchDelayMinutes ?? 0));
    setNotifyShopifyOnLabelCreated(provider.notifyShopifyOnLabelCreated ?? false);
    setCountry(provider.country ?? '');
    setTimezone(provider.timezone ?? '');
    setContactEmail(provider.contactEmail ?? '');
    setContactPhone(provider.contactPhone ?? '');
    setApiEndpoint(provider.apiEndpoint ?? '');
    setNotes(provider.notes ?? '');
    setCapabilitiesJson(JSON.stringify(provider.capabilities, null, 2));
  }, [provider]);

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      api.patch(`/api/v1/admin/providers/${provider.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'provider', provider.id] });
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    let capabilities: Record<string, unknown> | undefined;
    try {
      capabilities = JSON.parse(capabilitiesJson);
    } catch {
      return;
    }

    updateMutation.mutate({
      name,
      type,
      status,
      processingTime: Number(processingTime) || 1,
      // 0 is valid (dispatch immediately) — clamp to the API's 0–1440 range.
      dispatchDelayMinutes: Math.min(
        1440,
        Math.max(0, Math.trunc(Number(dispatchDelayMinutes) || 0)),
      ),
      notifyShopifyOnLabelCreated,
      country: country || undefined,
      timezone: timezone || undefined,
      contactEmail: contactEmail || undefined,
      contactPhone: contactPhone || undefined,
      apiEndpoint: apiEndpoint || undefined,
      notes: notes || undefined,
      capabilities,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Input label="Name" value={name} onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)} required />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Select label="Type" options={TYPE_OPTIONS} value={type} onChange={(e: ChangeEvent<HTMLSelectElement>) => setType(e.target.value)} />
        <Select label="Status" options={STATUS_OPTIONS} value={status} onChange={(e: ChangeEvent<HTMLSelectElement>) => setStatus(e.target.value)} />
        <Input label="Processing Time (days)" type="number" value={processingTime} onChange={(e: ChangeEvent<HTMLInputElement>) => setProcessingTime(e.target.value)} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="sm:col-span-1">
          <Input
            label="Dispatch Delay (minutes)"
            type="number"
            min={0}
            max={1440}
            value={dispatchDelayMinutes}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setDispatchDelayMinutes(e.target.value)}
          />
          <p className="mt-1 text-xs text-text-tertiary">
            Cancellation grace window: orders wait this long after payment settlement before being sent to this provider. 0 = send immediately.
          </p>
        </div>
      </div>

      <div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-500"
            checked={notifyShopifyOnLabelCreated}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setNotifyShopifyOnLabelCreated(e.target.checked)
            }
          />
          <span>
            <span className="block text-sm font-medium text-text-primary">
              Push Shopify fulfillment on label creation
            </span>
            <span className="block text-xs text-text-tertiary">
              When enabled, the Shopify fulfillment (tracking + customer notification) is created
              as soon as this provider reports a purchased label, instead of waiting for the
              carrier handoff. The customer may be notified before the package is actually moving.
            </span>
          </span>
        </label>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input label="Country" value={country} onChange={(e: ChangeEvent<HTMLInputElement>) => setCountry(e.target.value)} placeholder="US" />
        <Input label="Timezone" value={timezone} onChange={(e: ChangeEvent<HTMLInputElement>) => setTimezone(e.target.value)} placeholder="America/New_York" />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input label="Contact Email" type="email" value={contactEmail} onChange={(e: ChangeEvent<HTMLInputElement>) => setContactEmail(e.target.value)} />
        <Input label="Contact Phone" value={contactPhone} onChange={(e: ChangeEvent<HTMLInputElement>) => setContactPhone(e.target.value)} />
      </div>

      <Input label="API Endpoint" value={apiEndpoint} onChange={(e: ChangeEvent<HTMLInputElement>) => setApiEndpoint(e.target.value)} placeholder="https://api.provider.com" />

      <div>
        <label className="block text-sm font-medium text-text-primary mb-1">Capabilities (JSON)</label>
        <textarea
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent font-mono"
          rows={5}
          value={capabilitiesJson}
          onChange={(e) => setCapabilitiesJson(e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-1">Notes</label>
        <textarea
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      {updateMutation.isError && (
        <div className="rounded-md bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
          {updateMutation.error?.message ?? 'Failed to update provider.'}
        </div>
      )}

      {updateMutation.isSuccess && (
        <div className="rounded-md bg-green-500/10 border border-green-500/20 px-4 py-3 text-sm text-green-400">
          Provider updated successfully.
        </div>
      )}

      <div className="flex justify-end">
        <Button variant="primary" type="submit" loading={updateMutation.isPending}>
          Save Changes
        </Button>
      </div>
    </form>
  );
}
